import {CombinedTimelineSubitems} from "./combinedTimelineSubitems";
import {htmlEscape} from "@/shared/utils/string";
import {
	AddWorkingTimeEvent,
	CombinedTimelineDayComponentConfig,
	DuplicateWorkingTimeRequestEvent,
	DuplicateWorkingTimeEvent,
	EditWorkingTimeEvent,
	EditWorkingTimeRequestEvent,
	ProjectTimeTimelineItemType,
	QuickSwitchWorkingTimeEvent,
	ShowWorkingTimeRequestEvent,
	ShowWorkingTimeEvent,
	StatusUpdatedEvent,
	WorkingTimeItem,
	WorkingTimeTimelineDayContent,
	WorkingTimeTimelineItem,
	WorkingTimeTimelineItemType,
	WorkingTimeTypeFilter
} from "../timelineTypes";
import {TimelineContextMenu} from "../timelineContextMenu";
import {
	EVENT_WORKING_TIME_ADD,
	EVENT_WORKING_TIME_DELETED,
	EVENT_WORKING_TIME_DUPLICATE,
	EVENT_WORKING_TIME_EDIT,
	EVENT_WORKING_TIME_QUICK_SWITCH,
	EVENT_WORKING_TIME_RESUME,
	EVENT_WORKING_TIME_SHOW,
	EVENT_WORKING_TIME_REQUEST_DUPLICATE,
	EVENT_WORKING_TIME_REQUEST_EDIT,
	EVENT_WORKING_TIME_REQUEST_SHOW,
	EVENT_WORKING_TIME_STATUS_UPDATED
} from "../timelineConstants";
import {
	buildContextMenu,
	buildCustomFields,
	buildDescriptionElement,
	buildTimelineTimes,
	buildValidationsTable,
	deleteWorkingTime,
	getQuickSwitchWorkingTimeProcedure,
	hasWorkingTimeTypesForVacation,
	hasWorkingTimeTypesForWorkingTime,
	hasWorkingTimeTypesToAdd,
	isWorkingTimeDeletable,
	isWorkingTimeDuplicateable,
	isWorkingTimeEditable,
	isWorkingTimeQuickSwitchable,
	isWorkingTimeResumable,
	isWorkingTimeStatusNumberEnabled,
	resumeWorkingTime,
	setWorkingTimeStatus,
	showWorkingTimeQuickSwitchMessage
} from "../timelineHelper";
import {showWorkingTimeBulkResult} from "@/shared/bulkOperationHelper";
import {WorkingTimeRequestUtil} from "@/shared/workingtime/workingTimeRequestUtil";
import {
	getWorkingTimeTypesForAddAbsence,
	getWorkingTimeTypesForAddWorkingTime,
	isValidationRelevant,
	isWorkingTimeTypeRecordable,
	shouldAddStatusNumber
} from "@/shared/securityHelper";
import {minutesToMillis} from "@/shared/utils/misc";
import {formatDuration, formatRunningDurationSince, parseISOZonedDateTime, Temporal} from "@/shared/utils/datefunctions";
import {CombinedRecordingStatus, WorkingTime, WorkingTimeBulkOperationResult, WorkingTimeRequestResponseModel} from "@/shared/timr";
import {DeletedStatus, DurationUnit, RunningTimeStatus, TimelineMode, WorkingTimeRequestStatus, WorkingTimeTypeCategory} from "@/shared/timrTypes";
import {toRecordedEntryStatus} from "@/shared/utils/recordMapper";

/**
 * Component to display the timeline items of a single day.
 * This component is used inside the <code>TimelineDay</code> component and is appended to the day container.
 * It renders all timeline items for the day like working times, running working times, gaps, overlaps and none working time items.
 * There are different types of these items:
 * - Working Time Items: Represent recorded working times with details like type, description, duration, break time and custom fields.
 * - Working Time Request Items: Represent pending minute-based requests in the same chronological timeline.
 * - Running Working Time Items: Represent currently running working times with live duration updates.
 * - Gap Items: Represent breaks or gaps between working times.
 * - Overlap Items: Indicate overlapping working times that need user attention.
 * - None Working Time Items: Represent periods without any working time, allowing users to add new working times.
 *
 * Each timeline item may contain subitems such as project times or running project times, which are rendered using the <code>TimelineSubitems</code> component.
 * For an overview of the timeline architecture, have a look into the UML diagram "timeline_web_components.png"
 *
 */
export class CombinedTimelineDayItems extends HTMLElement {
	private _timelineDay: WorkingTimeTimelineDayContent;
	private _config: CombinedTimelineDayComponentConfig;
	private _combinedRecordingStatus: CombinedRecordingStatus;
	private _timelineSubitemsComponents: CombinedTimelineSubitems[] = [];

	set timelineDay(day: WorkingTimeTimelineDayContent) {
		this._timelineDay = day;
	}

	get timelineDay(): WorkingTimeTimelineDayContent {
		return this._timelineDay;
	}

	set config(value: CombinedTimelineDayComponentConfig) {
		this._config = value;
	}

	get config(): CombinedTimelineDayComponentConfig {
		return this._config;
	}

	set combinedRecordingStatus(value: CombinedRecordingStatus) {
		this._combinedRecordingStatus = value;
		for (const subitems of this._timelineSubitemsComponents) {
			subitems.combinedRecordingStatus = value;
		}
	}

	get combinedRecordingStatus(): CombinedRecordingStatus {
		return this._combinedRecordingStatus;
	}

	/**
	 * Main entry point to render the timeline day items.
	 */
	public connectedCallback(): void {
		// timeline day (content) and config are required to render this component
		if (!this.timelineDay || !this.config) {
			return;
		}

		// sort items according to configuration (toggled via timeline control button and cached in session variable)
		const sortedItems = this.config.timelineSortDescending
			? this.timelineDay.items.slice().reverse()
			: this.timelineDay.items;

		this._timelineSubitemsComponents = [];
		const items = sortedItems
			.filter(item => item.type !== WorkingTimeTimelineItemType.NONE || item.subitems.length !== 0)
			.map(item => {
				switch (item.type) {
					case WorkingTimeTimelineItemType.NONE:
						return this.buildNoneWorkingTimeItem(item);
					case WorkingTimeTimelineItemType.RUNNING:
						return this.buildRunningItem(item);
					case WorkingTimeTimelineItemType.WORKING_TIME:
						return this.buildWorkingTimeItem(item);
					case WorkingTimeTimelineItemType.WORKING_TIME_REQUEST:
						return this.buildWorkingTimeRequestItem(item);
					case WorkingTimeTimelineItemType.GAP:
						return this.buildGapItem(item);
					case WorkingTimeTimelineItemType.OVERLAP:
						return this.buildOverlapItem(item);
				}
			})
			.filter(Boolean);

		this.append(...items);
	}

	private buildRunningItem(item: WorkingTimeTimelineItem): HTMLElement {
		const {runningWorkingTime} = item;

		const escapedWorkingTimeTypeName = htmlEscape(runningWorkingTime.workingTimeType.name);

		const div = document.createElement("div");
		div.classList.add("item-running");
		div.innerHTML =
			`
			<!-- timestamps and circles -->
			${buildTimelineTimes(
				parseISOZonedDateTime(item.start),
				null,
				this.timelineDay.showTimeZone,
				this.config.timelineSortDescending
			)}
			<div class="panel panel-running border-color border-${runningWorkingTime.workingTimeType.category}">
				<!-- working time -->
				<div class="panel-heading">
					<div class="item-working-time-type">${escapedWorkingTimeTypeName}</div>
					<div class="duration-pulse">
						${runningWorkingTime.status === RunningTimeStatus.RUNNING ? `<span class="pulse-recording"></span>` : ""}
						${runningWorkingTime.status === RunningTimeStatus.PAUSED ? `<i class="fa fa-pause"></i>` : ""}
					</div>
					<span id="runningWorkingTimeDuration">${formatRunningDurationSince(runningWorkingTime)}</span>
				</div>

				<!-- custom fields -->
				${buildCustomFields(this.config.customFieldDefinitionsWorkingTime, runningWorkingTime).outerHTML}

				<!-- subitems -->
				<div class="panel-body"></div>
			</div>`;

		// add description if available (in weekly mode it is displayed in extra row since the space is limited, in daily in column via flexbox)
		const descriptionElement = buildDescriptionElement(runningWorkingTime);
		if (descriptionElement) {
			if (this.config.mode === TimelineMode.Daily) {
				div.querySelector(".item-working-time-type").after(descriptionElement);
			} else {
				div.querySelector(".panel-body").prepend(descriptionElement);
			}
		}
		this.styleTimeCircles(div, item);
		div.querySelector(".panel-body").append(this.buildSubitems(item));

		return div;
	}

	private buildWorkingTimeItem(item: WorkingTimeTimelineItem): HTMLElement {
		const {workingTime} = item;
		const div = document.createElement("div");

		if (workingTime.editUnit !== DurationUnit.Minutes) {
			return div;
		}

		div.dataset.toggle = "context";
		div.classList.add("item-working-time");

		const escapedWorkingTimeTypeName = htmlEscape(workingTime.workingTimeType.name);
		const isRecordWithPositionBreak = workingTime.workingTimeType.category === WorkingTimeTypeCategory.BreakTime;
		const positionAvailable =
			workingTime.startPosition?.id && workingTime.startPosition?.deletedStatus === DeletedStatus.NOT_DELETED ||
			workingTime.endPosition?.id && workingTime.endPosition?.deletedStatus === DeletedStatus.NOT_DELETED;

		div.innerHTML =
			`
			<!-- timestamps and circles -->
			${buildTimelineTimes(
				parseISOZonedDateTime(item.start),
				parseISOZonedDateTime(item.end),
				this.timelineDay.showTimeZone,
				this.config.timelineSortDescending
			)}
			<div class="panel border-color border-${workingTime.workingTimeType.category} ${workingTime.conflicted ? 'conflicted' : ''}">
				<!-- working time -->
				<div class="panel-heading">
					<div class="item-working-time-type">${escapedWorkingTimeTypeName}</div>
					<div class="breaktime-container" style="${workingTime.breakTime > 0 ? "" : "display: none"}">
						<i class="fal fa-coffee position-left"></i>${formatDuration(minutesToMillis(workingTime.breakTime))}
					</div>
					<div class="duration-container">
						<div class="display-flex">
							${workingTime.changed ? "<i class='fal fa-pen'></i>" : ""}
							${!isRecordWithPositionBreak || item.duration > 0 ? formatDuration(item.duration) : ""}
						</div>
						<div class="info-icons-container">
							${positionAvailable ? "<i class='far fa-map-marker-alt'></i>" : ""}
							<i class='far fa-status-${toRecordedEntryStatus(workingTime.status)}'></i>
						</div>
					</div>
					${buildContextMenu().outerHTML}
				</div>

				<!-- custom fields -->
				${buildCustomFields(this.config.customFieldDefinitionsWorkingTime, workingTime).outerHTML}

				<!-- subitems -->
				<div class="panel-body"></div>

				<!-- validationResults -->
				${buildValidationsTable(item.validationResults.filter(v => isValidationRelevant(v)), workingTime.id).outerHTML}
			</div>`;

		// add description if available (in weekly mode it is displayed in extra row since the space is limited, in daily in column via flexbox)
		const descriptionElement = buildDescriptionElement(workingTime);
		if (descriptionElement) {
			if (this.config.mode === TimelineMode.Daily) {
				div.querySelector(".item-working-time-type").after(descriptionElement);
			} else {
				div.querySelector(".panel-body").prepend(descriptionElement);
			}
		}

		this.styleTimeCircles(div, item);
		div.querySelector(".panel-body").append(this.buildSubitems(item));

		const contextMenu = new TimelineContextMenu<WorkingTime>();
		if (this.config.recordingFunctionsActive && !isRecordWithPositionBreak) {
			contextMenu
				.addMenuEntry({
					label: messages.withArguments("timeline.resume", workingTime.workingTimeType.name),
					show: workingTime => isWorkingTimeTypeRecordable(workingTime.workingTimeType.recordingModeUser) && isWorkingTimeResumable(this.combinedRecordingStatus, item, workingTime),
					action: workingTime => {
						resumeWorkingTime(this.combinedRecordingStatus, item, workingTime)?.done(() => this.onWorkingTimeResumed());
					}
				})
				.addMenuEntry({
					label: messages.withArguments("timeline.quick.start", workingTime.workingTimeType.name),
					show: () => isWorkingTimeTypeRecordable(workingTime.workingTimeType.recordingModeUser) && showWorkingTimeQuickSwitchMessage("timeline.quick.start", this.combinedRecordingStatus, item),
					active: () => isWorkingTimeQuickSwitchable(this.combinedRecordingStatus, item),
					action: workingTime => this.onQuickSwitchWorkingTimeClicked(item, workingTime)
				})
				.addMenuEntry({
					label: messages.withArguments("timeline.quick.switch", workingTime.workingTimeType.name),
					show: () => isWorkingTimeTypeRecordable(workingTime.workingTimeType.recordingModeUser) && showWorkingTimeQuickSwitchMessage("timeline.quick.switch", this.combinedRecordingStatus, item),
					active: () => isWorkingTimeQuickSwitchable(this.combinedRecordingStatus, item),
					action: workingTime => this.onQuickSwitchWorkingTimeClicked(item, workingTime)
				})
				.addMenuEntry({
					label: messages.withArguments("timeline.quick.switch.stop.BOTH", workingTime?.workingTimeType.name, this.combinedRecordingStatus?.projectTimeStatus?.runningProjectTime?.task.name),
					show: () => isWorkingTimeTypeRecordable(workingTime.workingTimeType.recordingModeUser) && showWorkingTimeQuickSwitchMessage("timeline.quick.switch.stop.BOTH", this.combinedRecordingStatus, item),
					active: () => isWorkingTimeQuickSwitchable(this.combinedRecordingStatus, item),
					action: workingTime => this.onQuickSwitchWorkingTimeClicked(item, workingTime)
				})
				.addDivider();
		}
		contextMenu
			.addMenuEntry({
				label: "timeline.open",
				action: workingTime => this.onShowWorkingTimeClicked({
					workingTime: workingTime,
					validationResults: item.validationResults
				})
			})
			.addMenuEntry({
				label: "timeline.edit",
				active: workingTime => isWorkingTimeEditable(workingTime),
				action: workingTime => this.onEditWorkingTimeClicked({
					workingTime: workingTime,
					validationResults: item.validationResults
				})
			})
			.addMenuEntry({
				label: "report.duplicate",
				active: workingTime => isWorkingTimeDuplicateable(workingTime),
				action: workingTime => this.onDuplicateWorkingTimeClicked({
					workingTime: workingTime,
					validationResults: item.validationResults
				})
			})
			.addMenuEntry({
				label: "report.delete",
				active: workingTime => isWorkingTimeDeletable(workingTime),
				action: workingTime => {
					deleteWorkingTime(workingTime)
						.done(() => this.dispatchEvent(new CustomEvent(EVENT_WORKING_TIME_DELETED, {bubbles: true})));
				}
			});

		const workingTimeStatusNumber = toRecordedEntryStatus(workingTime.status);

		// add divider if at least one status context menu entry is shown
		// DEVNOTE: It is very likely that the use and the delivered value of the variable `isTaskLeader` is basically not implemented correctly and consistently; see documentation of method
		if (this.config.workingTimeStatusNumbers.some(statusNumber => shouldAddStatusNumber(statusNumber, workingTimeStatusNumber, false))) {
			contextMenu.addDivider();
		}

		for (const statusNumber of this.config.workingTimeStatusNumbers) {
			const statusName = messages["reports.filter.status." + statusNumber];
			const title = messages.withArguments("report.setStatus", statusName);

			contextMenu.addMenuEntry({
				label: title,
				// DEVNOTE: It is very likely that the use and the delivered value of the variable `isTaskLeader` is basically not implemented correctly and consistently; see documentation of method
				show: () => shouldAddStatusNumber(statusNumber, workingTimeStatusNumber, false),
				action: workingTime => {
					setWorkingTimeStatus(workingTime, statusNumber)
						.done((result: WorkingTimeBulkOperationResult) => {
							if (!result.allUpdated) {
								showWorkingTimeBulkResult(result, "report.setStatus.");
							}
							if (result.updated > 0) {
								this.onStatusUpdated(statusName)
							}
						});
				},
				active: workingTime => isWorkingTimeStatusNumberEnabled(workingTime, statusNumber)
			});
		}
		contextMenu.bindToElement(div, workingTime);

		div.addEventListener("click", (e) => {
			if (e.target instanceof HTMLElement) {
				if (!e.target.closest("[class^='subitem-']") && !e.target.closest(".dropdown-menu")) {
					this.onShowWorkingTimeClicked({workingTime: workingTime, validationResults: item.validationResults});
				}
			}
		});

		return div;
	}

	private buildWorkingTimeRequestItem(item: WorkingTimeTimelineItem): HTMLDivElement {
		const {workingTimeRequest} = item;
		const div = document.createElement("div");

		if (!workingTimeRequest) {
			return div;
		}

		const escapedWorkingTimeTypeName = htmlEscape(workingTimeRequest.workingTimeType.name);
		const escapedDescription = htmlEscape(workingTimeRequest.description);
		const requestStatus = messages["workingTimeRequestStatus." + workingTimeRequest.status];

		div.dataset.toggle = "context";
		div.classList.add("item-working-time-request");
		div.innerHTML = `
				<!-- timestamps and circles -->
				${buildTimelineTimes(
					parseISOZonedDateTime(item.start),
					parseISOZonedDateTime(item.end),
					this.timelineDay.showTimeZone,
					this.config.timelineSortDescending
				)}
				<div class="panel panel-requests panel-minute-based-request border-color border-${workingTimeRequest.workingTimeType.category}">
					<div class="panel-heading">
						<div>
							<div class="item-working-time-type">${escapedWorkingTimeTypeName}</div>
							${workingTimeRequest.breakTimeManual > 0 ? `
								<div class="breaktime-container">
									<i class="fal fa-coffee position-left"></i>${formatDuration(minutesToMillis(workingTimeRequest.breakTimeManual))}
								</div>` : ""}
							<div class="duration-container">
								<div class="display-flex">${formatDuration(item.duration)}</div>
								<div class="info-icons-container"></div>
							</div>
							${buildContextMenu().outerHTML}
						</div>
						<div class="request-status-container">
							<span class="label request-status-${workingTimeRequest.status} request-category-${workingTimeRequest.workingTimeType.category}">
								${requestStatus}
							</span>
						</div>
						<div class="description-container" style="${!escapedDescription ? "display: none" : ""}">
							<i class="fal fa-comment-alt position-left"></i>
							<span class="description-clamped">${escapedDescription}</span>
						</div>
					</div>

					<!-- custom fields -->
					${buildCustomFields(this.config.customFieldDefinitionsWorkingTime, workingTimeRequest).outerHTML}

					<!-- subitems -->
					<div class="panel-body"></div>
				</div>`;

		this.styleTimeCircles(div, item);
		div.querySelector(".panel-body").append(this.buildSubitems(item));
		this.buildWorkingTimeRequestContextMenu(div, workingTimeRequest);

		div.addEventListener("click", (event) => {
			// Nested timeline items and context menu actions handle the click themselves and must not also open the request dialog.
			if (!(event.target instanceof HTMLElement) || event.target.closest("combined-timeline-subitems, .dropdown-menu")) {
				return;
			}
			this.onWorkingTimeRequestClicked(workingTimeRequest);
		});

		return div;
	}

	private onWorkingTimeRequestClicked(workingTimeRequest: WorkingTimeRequestResponseModel): void {
		const eventName = workingTimeRequest.status === WorkingTimeRequestStatus.ApprovalRequested
			? EVENT_WORKING_TIME_REQUEST_EDIT
			: EVENT_WORKING_TIME_REQUEST_SHOW;
		this.dispatchEvent(new CustomEvent<ShowWorkingTimeRequestEvent | EditWorkingTimeRequestEvent>(
			eventName, {
				detail: {workingTimeRequest},
				bubbles: true
			}
		));
	}

	private buildWorkingTimeRequestContextMenu(div: HTMLElement, workingTimeRequest: WorkingTimeRequestResponseModel): void {
		const contextMenu = new TimelineContextMenu<WorkingTimeRequestResponseModel>()
			.addMenuEntry({
				label: messages["requests.workingTimeRequestsUser.context.show"],
				action: request => this.dispatchEvent(new CustomEvent<ShowWorkingTimeRequestEvent>(
					EVENT_WORKING_TIME_REQUEST_SHOW, {
						detail: {workingTimeRequest: request},
						bubbles: true
					}
				))
			});

		contextMenu.addMenuEntry({
			label: messages["requests.workingTimeRequestsUser.context.edit"],
			action: request => this.dispatchEvent(new CustomEvent<EditWorkingTimeRequestEvent>(
				EVENT_WORKING_TIME_REQUEST_EDIT, {
					detail: {workingTimeRequest: request},
					bubbles: true
				}
			)),
			active: request => request.status === WorkingTimeRequestStatus.ApprovalRequested
		})
			.addMenuEntry({
			label: messages["requests.workingTimeRequestsUser.context.duplicate"],
			action: request => this.dispatchEvent(new CustomEvent<DuplicateWorkingTimeRequestEvent>(
				EVENT_WORKING_TIME_REQUEST_DUPLICATE, {
					detail: {workingTimeRequest: request},
					bubbles: true
				}
			))
		});

		contextMenu.addMenuEntry({
			label: messages["requests.workingTimeRequestsUser.context.delete"],
			action: request => this.deleteWorkingTimeRequest(request),
			active: request => WorkingTimeRequestUtil.isInApprovalProcess(request)
		})
			.bindToElement(div, workingTimeRequest);
	}

	private deleteWorkingTimeRequest(workingTimeRequest: WorkingTimeRequestResponseModel): void {
		WorkingTimeRequestUtil.deleteWorkingTimeRequest(workingTimeRequest,
			() => this.dispatchEvent(new CustomEvent(EVENT_WORKING_TIME_DELETED, {bubbles: true})));
	}

	private buildNoneWorkingTimeItem(item: WorkingTimeTimelineItem): HTMLDivElement {
		const {subitems} = item;
		const div = document.createElement("div");

		if (item.subitems.length === 0) {
			return div;
		}

		if (this.timelineDay.showTimeZone) {
			div.classList.add("show-timezone");
		}
		div.classList.add("item-none");
		div.innerHTML =
			`
			<div class="panel panel-none">
				${hasWorkingTimeTypesToAdd(this.config) ?
				`<div class="panel-heading">
					<div class="dropdown" data-context-menu-container>
						<span class="btn btn-link btn-link-primary btn-xs no-padding-left" data-context-menu-button>
							<i class="fal fa-plus position-left"></i> ${messages["timeline.workingTime.add"]}
						</span>
					</div>
				</div>` : ""}
				<div class="panel-body"></div>
			</div>`;

		new TimelineContextMenu()
			.addMenuEntry({
				label: "timeline.workingTime.add",
				action: () => this.onAddWorkingTimeEventClicked(
					getWorkingTimeTypesForAddWorkingTime,
					parseISOZonedDateTime(subitems[0].start),
					parseISOZonedDateTime(subitems[subitems.length - 1].end)
				),
				active: () => hasWorkingTimeTypesForWorkingTime(this.config)
			})
			.addMenuEntry({
				label: "timeline.workingTime.add.absence",
				action: () => this.onAddWorkingTimeEventClicked(
					getWorkingTimeTypesForAddAbsence,
					parseISOZonedDateTime(subitems[0].start),
					parseISOZonedDateTime(subitems[subitems.length - 1].end)
				),
				active: () => hasWorkingTimeTypesForVacation(this.config)
			}).bindToElement(div);

		div.querySelector(".panel-body").append(this.buildSubitems(item));

		return div;
	}

	private buildGapItem(item: WorkingTimeTimelineItem): HTMLDivElement {
		const div = document.createElement("div");

		if (this.timelineDay.showTimeZone) {
			div.classList.add("show-timezone");
		}
		div.classList.add("item-gap");
		div.innerHTML = `
					<div class="panel panel-gap">
						<div class="dropdown ${hasWorkingTimeTypesToAdd(this.config) ? '' : 'disabled'}" data-context-menu-container>
							<span class="btn btn-link btn-link-primary btn-xs" data-context-menu-button>
								<i class="fal fa-plus position-left"></i>
							</span>
						</div>
						<div><i class="fal fa-coffee position-left"></i> ${messages["timeline.workingTime.break"]}</div>
						<div class="duration-container">${formatDuration(item.duration)}</div>
					</div>`;

		new TimelineContextMenu()
			.addMenuEntry({
				label: "timeline.workingTime.add",
				action: () => this.onAddWorkingTimeEventClicked(getWorkingTimeTypesForAddWorkingTime, parseISOZonedDateTime(item.start), parseISOZonedDateTime(item.end)),
				active: () => hasWorkingTimeTypesForWorkingTime(this.config)
			})
			.addMenuEntry({
				label: "timeline.workingTime.add.absence",
				action: () => this.onAddWorkingTimeEventClicked(getWorkingTimeTypesForAddAbsence, parseISOZonedDateTime(item.start), parseISOZonedDateTime(item.end)),
				active: () => hasWorkingTimeTypesForVacation(this.config)
			}).bindToElement(div);

		if (item.subitems.length > 0) {
			const subitems = document.createElement("div");
			subitems.classList.add("panel", "panel-none");
			subitems.innerHTML = `
				${hasWorkingTimeTypesToAdd(this.config) ?
				`<div class="panel-heading">
					<div class="dropdown" data-context-menu-container>
						<span class="btn btn-link btn-link-primary btn-xs no-padding-left" data-context-menu-button>
							<i class="fal fa-plus position-left"></i> ${messages["timeline.workingTime.add"]}
						</span>
					</div>
				</div>` : ""}
				<div class="panel-body"></div>`;

			subitems.querySelector(".panel-body").appendChild(this.buildSubitems(item));
			div.appendChild(subitems);

			new TimelineContextMenu()
				.addMenuEntry({
					label: "timeline.workingTime.add",
					action: () => this.onAddWorkingTimeEventClicked(getWorkingTimeTypesForAddWorkingTime, parseISOZonedDateTime(item.start), parseISOZonedDateTime(item.end)),
					active: () => hasWorkingTimeTypesForWorkingTime(this.config)
				})
				.addMenuEntry({
					label: "timeline.workingTime.add.absence",
					action: () => this.onAddWorkingTimeEventClicked(getWorkingTimeTypesForAddAbsence, parseISOZonedDateTime(item.start), parseISOZonedDateTime(item.end)),
					active: () => hasWorkingTimeTypesForVacation(this.config)
				}).bindToElement(subitems);
		}

		return div;
	}

	private buildOverlapItem(item: WorkingTimeTimelineItem): HTMLDivElement {
		const div = document.createElement("div");

		if (this.timelineDay.showTimeZone) {
			div.classList.add("show-timezone");
		}
		div.classList.add("item-overlap");
		div.innerHTML = `
						<div class="panel panel-overlap">
							<div><i class="fa fa-validation-severity-MINOR position-left" title="${messages["recordingValidation.severity.MINOR"]}"></i></div>
							<div>${messages["validationResult.entryOverlap.workingTime"]}</div>
							<div class="duration-container">${formatDuration(item.duration)}</div>
						</div>`;
		return div;
	}

	private styleTimeCircles(li: HTMLDivElement, item: WorkingTimeTimelineItem): void {
		if (this.isStartEqual(item)) {
			li.querySelector(".times").classList.add("subitem-start");
		}
		if (this.isEndEqual(item)) {
			li.querySelector(".times").classList.add("subitem-end");
		}
	}

	private buildSubitems(item: WorkingTimeTimelineItem): CombinedTimelineSubitems {
		const subitemsComponent = document.createElement("combined-timeline-subitems") as CombinedTimelineSubitems;

		subitemsComponent.content = {
			workingTimeItem: item,
			validationResults: this.timelineDay.validationResults,
			subitems: item.subitems,
			showTimeZone: this.timelineDay.showTimeZone
		};
		subitemsComponent.config = {
			customFieldDefinitionsProjectTime: this.config.customFieldDefinitionsProjectTime,
			recordingFunctionsActive: this.config.recordingFunctionsActive,
			projectTimeStatusNumbers: this.config.projectTimeStatusNumbers,
			mode: this.config.mode,
			projectTimeAttachmentsEnabled: this.config.projectTimeAttachmentsEnabled,
			timelineSortDescending: this.config.timelineSortDescending
		};
		subitemsComponent.combinedRecordingStatus = this.combinedRecordingStatus;

		this._timelineSubitemsComponents.push(subitemsComponent);

		return subitemsComponent;
	}

	private onAddWorkingTimeEventClicked(workingTimeTypeFilter: WorkingTimeTypeFilter, start: Temporal.ZonedDateTime | null, end: Temporal.ZonedDateTime | null): void {
		const customEvent = new CustomEvent<AddWorkingTimeEvent>(
			EVENT_WORKING_TIME_ADD, {
				detail: {
					workingTimeTypeFilter: workingTimeTypeFilter,
					start: start,
					end: end
				},
				bubbles: true
			}
		);

		this.dispatchEvent(customEvent);
	}

	private onWorkingTimeResumed(): void {
		this.dispatchEvent(new CustomEvent(EVENT_WORKING_TIME_RESUME, {bubbles: true}));
	}

	private onQuickSwitchWorkingTimeClicked(item: WorkingTimeTimelineItem, workingTime: WorkingTime): void {
		const event = new CustomEvent<QuickSwitchWorkingTimeEvent>(
			EVENT_WORKING_TIME_QUICK_SWITCH, {
				detail: {
					quickSwitchProcedure: getQuickSwitchWorkingTimeProcedure(this.combinedRecordingStatus, item),
					workingTime: workingTime
				},
				bubbles: true
			}
		);

		this.dispatchEvent(event);
	}

	private onShowWorkingTimeClicked(workingTimeItem: WorkingTimeItem): void {
		const event = new CustomEvent<ShowWorkingTimeEvent>(
			EVENT_WORKING_TIME_SHOW, {
				detail: {
					workingTimeItem: workingTimeItem
				},
				bubbles: true
			}
		);

		this.dispatchEvent(event);
	}

	private onEditWorkingTimeClicked(workingTimeItem: WorkingTimeItem): void {
		const event = new CustomEvent<EditWorkingTimeEvent>(
			EVENT_WORKING_TIME_EDIT, {
				detail: {
					workingTimeItem: workingTimeItem
				},
				bubbles: true
			}
		);

		this.dispatchEvent(event);
	}

	private onDuplicateWorkingTimeClicked(workingTimeItem: WorkingTimeItem): void {
		const event = new CustomEvent<DuplicateWorkingTimeEvent>(
			EVENT_WORKING_TIME_DUPLICATE, {
				detail: {
					workingTimeItem: workingTimeItem
				},
				bubbles: true
			}
		);

		this.dispatchEvent(event);
	}

	private onStatusUpdated(statusName: string): void {
		const event = new CustomEvent<StatusUpdatedEvent>(
			EVENT_WORKING_TIME_STATUS_UPDATED, {
				detail: {
					statusName: statusName
				},
				bubbles: true
			}
		);
		this.dispatchEvent(event);
	}

	private isStartEqual(item: WorkingTimeTimelineItem): boolean {
		return item.subitems
			.filter(subitem => subitem.type === ProjectTimeTimelineItemType.PROJECT_TIME || subitem.type === ProjectTimeTimelineItemType.RUNNING)
			.some(subitem => subitem.start === item.start);
	}

	private isEndEqual(item: WorkingTimeTimelineItem): boolean {
		return item.subitems
			.filter(subitem => subitem.type === ProjectTimeTimelineItemType.PROJECT_TIME || subitem.type === ProjectTimeTimelineItemType.RUNNING)
			.some(subitem => subitem.end === item.end);
	}
}
